import { useEffect, useState } from "react";
import { Info } from "lucide-react";
import { MONTHS, fmtSEK, g } from "../planning/revenue/format";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../ui/sheet";

const LINE_TITLES = {
  tm: "Time & Material",
  projects: "Projects",
  services: "Services",
  other: "Other Revenue",
  total_sales: "Total Sales",
  factoring: "Factoring (6064)",
  employee_direct: "Employee Consultants",
  subcontractor_direct: "Subcontractors",
  total_direct: "Total Direct Costs",
  gross_profit: "Gross Profit",
  regular_indirect: "Regular Indirect Costs",
  overhead_personnel: "Overhead Personnel Costs",
  group_overhead: "Group Overhead (6591)",
  depreciation: "Depreciation",
  interest: "Interest",
  total_indirect: "Total Indirect Costs",
  pbt: "Profit Before Corporate Tax",
  corporate_tax: "Corporate Tax",
  profit_after_tax: "Profit After Tax",
  pass_through_revenue: "Customer Pass-through Revenue",
  pass_through_cost: "Customer Pass-through Cost",
  overhead_recharge: "Group Overhead Recharge",
};
const REVENUE_LINES = new Set(["tm", "projects", "services", "other"]);
const COST_LINES = new Set(["factoring", "employee_direct", "subcontractor_direct", "regular_indirect", "overhead_personnel", "group_overhead", "depreciation", "interest", "total_direct", "total_indirect"]);

function Row({ label, value, sub, testId, strong, eliminated }) {
  return (
    <div className="flex items-baseline justify-between py-1.5 border-b border-slate-100" data-testid={testId}>
      <div>
        <div className={`text-sm ${strong ? "font-bold text-slate-900" : "text-slate-800"}`}>{label}</div>
        {sub && <div className="text-xs text-slate-400">{sub}</div>}
      </div>
      <div className={`text-sm font-semibold ${eliminated ? "text-[#B91C1C]" : "text-slate-900"}`}>
        {value == null ? "—" : `${fmtSEK(value)} kr`}
      </div>
    </div>
  );
}

// Build 9A — consolidated drilldown: which legal companies contributed, which
// amounts were eliminated (explicit IC transactions only), which external
// amounts remain, and which actual import / forecast source is authoritative.
export default function ConsolidatedPnlDrilldown({ open, onClose, line, month, data, year }) {
  const [m, setM] = useState(month);
  useEffect(() => {
    setM(month);
  }, [month, line, open]);

  if (!data || !line || !LINE_TITLES[line]) {
    return <Sheet open={open} onOpenChange={(v) => !v && onClose()} />;
  }
  const cell = g(data.months, m) || {};
  const elim = cell.eliminations || {};
  const stateOf = (cid) => (data.per_company.find((c) => c.company_id === cid)?.months?.[String(m)] || {});

  let body = null;
  if (REVENUE_LINES.has(line)) {
    const rows = data.drilldown?.revenue?.[line]?.[String(m)] || [];
    const entries = (data.drilldown?.pass_through_entries?.[String(m)] || []).filter((e) => e.pnl_line === line);
    const bookedOps = new Set((cell.ic_booked_detail?.ops || []).map((o) => o.company_id));
    body = (
      <>
        {rows.map((r) => {
          const booked = bookedOps.has(r.company_id);
          const internal = booked ? (r.value || 0) : (r.internal || 0);
          return (
            <Row
              key={r.company_id}
              label={r.company_name}
              sub={`${stateOf(r.company_id).actual_source === "ACCOUNTING_IMPORT" ? "Accounting actuals · committed import" : stateOf(r.company_id).state === "ACTUAL" ? "Closed · actual" : "Open · forecast"}${internal ? ` · incl. internal ${fmtSEK(internal)} kr (eliminated${booked ? ", booked IC" : ""})` : ""}`}
              value={r.value}
              testId={`cpnl-dd-row-${r.company_id}`}
            />
          );
        })}
        {entries.length > 0 && (
          <div className="mt-3" data-testid="cpnl-dd-pt-entries">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Intercompany pass-through (eliminated)</p>
            {entries.map((e, i) => (
              <Row key={i} label={e.label} sub={`${e.company_name} → Group · explicit transaction`} value={-e.value} eliminated testId={`cpnl-dd-pt-${i}`} />
            ))}
          </div>
        )}
        <div className="bg-[#EBF3FE] border border-[#CFE3FC] rounded-lg p-3 flex gap-2 mt-3" data-testid="cpnl-dd-rev-note">
          <Info className="h-4 w-4 text-[#0052CC] shrink-0 mt-0.5" />
          <p className="text-xs text-slate-600">
            Internal revenue eliminated this month: {fmtSEK(elim.ic_revenue || 0)} kr
            {(elim.ic_revenue_booked || 0) !== 0 ? ` + ${fmtSEK(elim.ic_revenue_booked)} kr booked intercompany revenue (committed accounting actuals, eliminated at booked amounts)` : ""}.
            The external customer revenue invoiced by the Group company remains — it is never eliminated (zero-margin pass-through).
          </p>
        </div>
      </>
    );
  } else if (line === "group_overhead") {
    const rows = data.drilldown?.lines?.group_overhead?.[String(m)] || [];
    body = (
      <>
        {rows.map((r) => (
          <Row key={r.company_id} label={`${r.company_name} — 6591 allocation`} sub="Internal recharge cost (eliminated)" value={r.value} testId={`cpnl-dd-row-${r.company_id}`} />
        ))}
        <Row label="Group Overhead eliminations" sub="Hub recharge revenue vs operating-company 6591" value={-(elim.oh_recharge || 0)} eliminated testId="cpnl-dd-oh-elim" />
        <div className="bg-[#EBF3FE] border border-[#CFE3FC] rounded-lg p-3 flex gap-2 mt-3" data-testid="cpnl-dd-oh-note">
          <Info className="h-4 w-4 text-[#0052CC] shrink-0 mt-0.5" />
          <p className="text-xs text-slate-600">
            The real Group overhead cost remains exactly once — in the Group company's Overhead Personnel Costs and
            Regular Indirect Costs. Only the internal recharge relationship is eliminated.
          </p>
        </div>
      </>
    );
  } else if (line === "corporate_tax" || line === "profit_after_tax") {
    body = (
      <>
        <div className="bg-[#EBF3FE] border border-[#CFE3FC] rounded-lg p-3 flex gap-2 mb-3" data-testid="cpnl-dd-tax-note">
          <Info className="h-4 w-4 text-[#0052CC] shrink-0 mt-0.5" />
          <p className="text-xs text-slate-600">{data.tax_note}</p>
        </div>
        {data.per_company.map((c) => (
          <Row
            key={c.company_id}
            label={c.company_name}
            sub={line === "corporate_tax" ? "Legal-entity corporate tax (FY)" : "Legal-entity Profit After Tax (FY)"}
            value={c.fy[line]}
            testId={`cpnl-dd-tax-${c.company_id}`}
          />
        ))}
      </>
    );
  } else if (line === "pass_through_revenue" || line === "pass_through_cost" || line === "overhead_recharge") {
    const notes = {
      pass_through_revenue: "External customer revenue invoiced by the Group company on behalf of operating companies — never eliminated in consolidation. The matching internal revenue of the operating company is what gets eliminated.",
      pass_through_cost: "Internal cost eliminated in consolidation against the operating company's matching internal revenue (explicit linked transaction, zero margin).",
      overhead_recharge: "Internal recharge revenue eliminated in consolidation against the operating companies' 6591 cost. The real underlying Group overhead remains once at the Group company.",
    };
    body = (
      <div className="bg-[#EBF3FE] border border-[#CFE3FC] rounded-lg p-3 flex gap-2" data-testid="cpnl-dd-icline-note">
        <Info className="h-4 w-4 text-[#0052CC] shrink-0 mt-0.5" />
        <p className="text-xs text-slate-600">{notes[line]}</p>
      </div>
    );
  } else {
    const rows = data.drilldown?.lines?.[line]?.[String(m)] || [];
    body = (
      <>
        {rows.map((r) => (
          <Row
            key={r.company_id}
            label={r.company_name}
            sub={stateOf(r.company_id).actual_source === "ACCOUNTING_IMPORT" ? "Accounting actuals · committed import" : stateOf(r.company_id).state === "ACTUAL" ? "Closed · actual" : "Open · forecast"}
            value={r.value}
            testId={`cpnl-dd-row-${r.company_id}`}
          />
        ))}
        {line === "total_direct" && (elim.ic_cost || 0) !== 0 && (
          <Row label="Intercompany cost eliminations" sub="Hub pass-through cost — back-to-back, zero margin" value={-elim.ic_cost} eliminated testId="cpnl-dd-ic-cost-elim" />
        )}
        {line === "total_direct" && (elim.ic_cost_booked || 0) !== 0 && (
          <Row label="Booked intercompany purchases (eliminated)" sub="Hub booked IC subcontractor cost vs operating booked IC revenue — at booked amounts" value={-elim.ic_cost_booked} eliminated testId="cpnl-dd-ic-cost-booked-elim" />
        )}
        {line === "pbt" && (cell.ic_booked_detail?.difference || 0) !== 0 && (
          <Row label="IC Booked Difference (named adjustment)" sub="Booked IC revenue vs booked IC cost mismatch — consolidation-only, requires Finance review" value={-cell.ic_booked_detail.difference} eliminated testId="cpnl-dd-ic-booked-diff" />
        )}
        {COST_LINES.has(line) && line === "total_indirect" && (elim.oh_recharge || 0) !== 0 && (
          <Row label="Group Overhead 6591 eliminations" sub="Internal recharge — real overhead remains at the Group company" value={-elim.oh_recharge} eliminated testId="cpnl-dd-oh-ind-elim" />
        )}
      </>
    );
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-[560px] sm:max-w-[560px] overflow-y-auto" data-testid="cpnl-drilldown">
        <SheetHeader>
          <SheetTitle className="font-heading">{LINE_TITLES[line]} — Consolidated</SheetTitle>
          <SheetDescription>
            {data.company_name} — {year}. Which legal companies contributed, what was eliminated, what remains external?
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 flex flex-wrap gap-1" data-testid="cpnl-dd-months">
          {MONTHS.map((label, i) => (
            <button
              key={i + 1}
              onClick={() => setM(i + 1)}
              className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                m === i + 1 ? "bg-[#0052CC] text-white border-[#0052CC]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
              }`}
              data-testid={`cpnl-dd-month-${i + 1}`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="mt-3 text-xs text-slate-500" data-testid="cpnl-dd-active-value">
          Consolidated {MONTHS[m - 1]}: <span className="font-semibold text-slate-900">{cell[line] == null ? "—" : `${fmtSEK(cell[line])} kr`}</span>
          <span className="ml-2">{cell.status === "ACTUAL" ? "All entities actual" : cell.status === "MIXED" ? "Mixed actual/forecast" : "Forecast"}</span>
        </div>

        <div className="mt-3 border border-slate-200 rounded-lg p-3" data-testid="cpnl-dd-breakdown">
          {body}
        </div>
      </SheetContent>
    </Sheet>
  );
}
